import React, { useState, useEffect } from 'react';
import { losApi } from '../../losApi';
import { CheckSquare, Plus } from 'lucide-react';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogTitle } from '../../../components/ui/dialog';
import { toast } from '../../../hooks/use-toast';
import { errorMessage } from '../../errors';
import { Link } from 'react-router-dom';
import ContactTasks from '../../ContactTasks';
import { ModuleHeader, TabBtn, Fld } from '../shared/formFields';
import { ProgramBakeoffCard } from './ProgramBakeoffCard';
import { TrackersCard } from './TrackersCard';
import { formatDate, formatDateTime, isDateOnly } from '../../kit/time';
import { ariveEvents, bfyStatusEvents, groupEvents } from '../shared/lenderEvents';

export function LoanCenterModule({ loan, onChange }) {
  const [tab, setTab] = useState('tasks');
  const [ms, setMs] = useState([]);
  const [msLoaded, setMsLoaded] = useState(false);
  const loadMs = () => losApi.listMilestones(loan.loan_file_id)
    .then(setMs)
    .catch(() => setMs([]))
    .finally(() => setMsLoaded(true));
  // A status move from the header stepper writes a milestone: refetch on it.
  useEffect(() => { loadMs(); }, [loan.loan_file_id, loan.status]);
  return (
    <div className="space-y-4">
      <ModuleHeader title="Loan Center" />
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <ProgramBakeoffCard loan={loan} />
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="flex items-center border-b border-slate-100 px-2">
              <TabBtn active={tab === 'tasks'} onClick={() => setTab('tasks')}>Tasks</TabBtn>
              <TabBtn active={tab === 'events'} onClick={() => setTab('events')}>Lender Events</TabBtn>
            </div>
            {tab === 'tasks' && <LoanTasks loan={loan} />}
            {tab === 'events' && <LenderEvents loan={loan} milestones={ms} loaded={msLoaded} />}
          </div>
          <MilestoneTimeline loan={loan} milestones={ms} onAdded={loadMs} />
        </div>
        <TlsLenderPanel loan={loan} onChange={onChange} />
      </div>
    </div>
  );
}

// Tasks hang off the borrower's contact, not the loan, so this lists that contact's
// tasks (the same list as the contact profile). Completing happens on the Tasks page.
function LoanTasks({ loan }) {
  if (!loan.contact_id) {
    return (
      <div className="p-8 text-center" data-testid="loan-tasks-no-contact">
        <CheckSquare size={32} className="mx-auto mb-2 text-slate-300" />
        <div className="text-[12px] text-slate-500">Link a contact to this loan to see and add tasks.</div>
      </div>
    );
  }
  return (
    <div className="p-4 space-y-3" data-testid="loan-tasks">
      <ContactTasks contactId={loan.contact_id} />
      <div className="text-[11px] text-slate-500">
        Tasks belong to the borrower's contact. Check them off on the{' '}
        <Link to="/portal/tasks" className="underline">Tasks page</Link>.
      </div>
    </div>
  );
}

const EVENT_DOT = { stage: '#070B5F', status: '#070B5F', date: '#6BA82E', tracker: '#0ea5e9', lock: '#f59e0b', archive: '#94a3b8' };
// Arive sends most dates as plain days (stored as UTC midnight): show the day,
// not a time that rolls back to the evening before west of UTC.
const when = (at) => (isDateOnly(at) ? formatDate(at) : formatDateTime(at));

function EventRow({ e }) {
  return (
    <div className="px-4 py-2.5 flex items-start gap-3" data-testid={`lender-event-${e.id}`}>
      <span className="mt-1.5 w-2 h-2 rounded-full shrink-0" style={{ background: EVENT_DOT[e.kind] || '#94a3b8' }} />
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-semibold" style={{ color: '#030524' }}>{e.title}</div>
        {e.detail && <div className="text-[11px] text-slate-500">{e.detail}</div>}
      </div>
      <span className="text-[11px] text-slate-400 whitespace-nowrap">{e.at ? when(e.at) : 'No date'}</span>
    </div>
  );
}

function EventGroup({ title, events, testId }) {
  if (events.length === 0) return null;
  return (
    <div data-testid={testId}>
      <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest font-bold text-slate-400">{title}</div>
      <div className="divide-y divide-slate-100">{events.map((e) => <EventRow key={e.id} e={e} />)}</div>
    </div>
  );
}

// Read-only. Arive-linked: what the Arive feed carries. BFY-originated: the
// loan's own status moves.
function LenderEvents({ loan, milestones, loaded }) {
  const linked = !!loan.arive;
  const events = linked ? ariveEvents(loan.arive) : bfyStatusEvents(milestones);
  const { upcoming, past, undated } = groupEvents(events);
  return (
    <div className="pb-3" data-testid="lender-events">
      <div className="px-4 pt-3 text-[11px] text-slate-500">
        {linked
          ? 'From Arive, read-only. Updates each time Arive sends this loan.'
          : 'Status moves on this loan.'}
      </div>
      {events.length === 0 && (linked || loaded) && (
        <div className="p-8 text-center text-[12px] text-slate-400 italic" data-testid="lender-events-empty">
          {linked ? 'Arive has not sent any events for this loan yet.' : 'No status moves yet.'}
        </div>
      )}
      <EventGroup title="Upcoming" events={upcoming} testId="lender-events-upcoming" />
      <EventGroup title="Past" events={past} testId="lender-events-past" />
      <EventGroup title="Not dated" events={undated} testId="lender-events-undated" />
    </div>
  );
}

function TlsLenderPanel({ loan, onChange }) {
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: '#6BA82E' }}>TLS Lender</div>
        <div className="text-[13px] font-semibold" style={{ color: '#030524' }}>{loan.rate_lock?.lender || 'Not Assigned'}</div>
        <div className="text-[11px] text-slate-500 mt-0.5">Lender Loan # {loan.rate_lock?.lender_loan_number || loan.arive?.lender_loan_identifier || '—'}</div>
        <div className="text-[11px] text-slate-500 mt-0.5">{loan.rate_lock?.product || '—'}</div>
        <button className="mt-3 w-full text-[12px] font-semibold px-3 py-2 rounded-lg text-white" style={{ background: '#070B5F' }}>
          Register Loan
        </button>
      </div>

      <TrackersCard loan={loan} onChange={onChange} />
    </div>
  );
}

function MilestoneTimeline({ loan, milestones: ms, onAdded }) {
  const [adding, setAdding] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="loan-history">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[13px] font-semibold" style={{ color: '#030524' }}>History</div>
        <button
          onClick={() => setAdding(true)}
          data-testid="add-milestone-btn"
          className="text-[12px] font-semibold px-3 py-1.5 rounded-lg text-white inline-flex items-center gap-1.5"
          style={{ background: '#070B5F' }}
        >
          <Plus size={12} /> Add milestone
        </button>
      </div>
      {adding && (
        <NewMilestoneModal
          loanId={loan.loan_file_id}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); onAdded(); }}
        />
      )}
      <div className="relative pl-6 border-l-2 border-slate-100 space-y-3">
        {ms.map((m) => (
          <div key={m.milestone_id} className="relative">
            <div className="absolute -left-[29px] top-1 w-4 h-4 rounded-full border-2 border-white shadow" style={{ background: '#6BA82E' }} />
            <div className="text-[10px] uppercase tracking-widest text-slate-400">{when(m.occurred_at)}</div>
            <div className="font-semibold text-[12px] mt-0.5" style={{ color: '#030524' }}>
              {m.label || m.key}
              {m.key === 'custom' && <span className="ml-2 text-[9px] font-bold uppercase tracking-wider text-slate-400" data-testid={`milestone-manual-${m.milestone_id}`}>Added by hand</span>}
            </div>
            {m.notes && <div className="text-[11px] text-slate-500 mt-0.5">{m.notes}</div>}
          </div>
        ))}
        {ms.length === 0 && <div className="text-[12px] text-slate-400 italic">No history yet.</div>}
      </div>
    </div>
  );
}

// Local calendar day as YYYY-MM-DD (the date input's format).
const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// A hand-recorded event (appraisal back, title ordered...). Status moves come
// from the status stepper, not from here.
function NewMilestoneModal({ loanId, onClose, onSaved }) {
  const [f, setF] = useState({ label: '', date: todayLocal(), notes: '' });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.label.trim()) return;
    setSaving(true);
    try {
      await losApi.addMilestone(loanId, 'custom', f.label.trim(), f.notes.trim(), f.date);
      onSaved();
    } catch (e) {
      toast({ title: 'Could not add the milestone', description: errorMessage(e) });
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogTitle className="font-serif text-lg font-bold mb-4" style={{ color: '#030524' }}>Add milestone</DialogTitle>
        <Fld label="What happened" value={f.label} onChange={(v) => setF((x) => ({ ...x, label: v.slice(0, 200) }))} placeholder="Appraisal received" testId="milestone-label" />
        <div className="mt-3">
          <Fld label="Date" type="date" max={todayLocal()} value={f.date} onChange={(v) => setF((x) => ({ ...x, date: v }))} testId="milestone-date" />
        </div>
        <div className="mt-3"><Label className="text-xs">Notes</Label>
          <Textarea rows={3} value={f.notes} maxLength={2000} onChange={(e) => setF((x) => ({ ...x, notes: e.target.value }))} className="mt-1" data-testid="milestone-notes" /></div>
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={saving || !f.label.trim()} className="text-sm px-4 py-2 rounded-lg text-white disabled:opacity-50" style={{ background: '#070B5F' }} data-testid="save-milestone">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
